import { useMetadata } from '@api/useMetadata'
import { useOrders } from '@api/useOrders'
import { useLocalStorage } from '@gravitate-js/the-armory'
import { useUserSettings } from '@hooks/settings'
import { useQueryClient } from '@tanstack/react-query'
import { getCurrentMarket, getMarketSectors, sortNumeric } from '@utils/general'
import _ from 'lodash'
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'

const AcceptedOrdersContext = createContext()

export function AcceptedOrdersContextProvider({ children }) {
  const [isDraggingActionBlock, setIsDraggingActionBlock] = useState(false)
  const [isRowDragEnabled, setIsRowDragEnabled] = useState(true)

  const queryClient = useQueryClient()
  const { useAcceptedOrdersQuery } = useOrders()
  const [ordersCollapsed, setOrdersCollapsed] = useState(false)
  const { value: selectedSectors, setValue: setSelectedSectors } = useLocalStorage('order-dispatch-sectors', [])
  const [selectedPriceType, setSelectedPriceType] = useState('')
  const [splitCandidatesFilter, setSplitCandidatesFilter] = useState(false)

  const payload = useMemo(() => {
    const market = getCurrentMarket()
    const p = { sectors: selectedSectors, price_type: selectedPriceType, only_split_candidates: splitCandidatesFilter }
    if (market && !selectedSectors.length) {
      p.market = ''
    }
    return p
  }, [selectedSectors, selectedPriceType, splitCandidatesFilter])

  const { data: ordersResponse, status: ordersStatus, refetch: refetchOrders } = useAcceptedOrdersQuery(payload)
  const { data: orderMetadata, status: metadataStatus } = useMetadata('order')

  const isLoading = useMemo(
    () => ['pending', 'fetching'].includes(ordersStatus) || ['pending', 'fetching'].includes(metadataStatus),
    [ordersStatus, metadataStatus]
  )

  const { updateSetting } = useUserSettings()

  const metadata = useMemo(() => {
    const orders = ordersResponse?.rows
    if (!orders || !orderMetadata) return {}
    return {
      ...orderMetadata,
      stores: sortNumeric([...new Set(orders?.flatMap((o) => o.drops.map((d) => d.location_name)))]),
      cities: _.uniq(orders?.map((o) => o.drops.map((d) => d.city)[0]))
        .filter((c) => !!c)
        .sort(),
      states: _.uniq(orders?.map((o) => o.drops.map((d) => d.state)[0]))
        .filter((s) => !!s)
        .sort(),
      terminals: _.uniq(orders?.map((o) => o.loads.map((l) => l.location_name)[0])).sort(),
      products: _.uniq(orders?.map((o) => o.product_ratios.map((p) => p.name)[0])).sort(),
    }
  }, [ordersResponse, orderMetadata])
  const sectors = useMemo(() => orderMetadata?.sectors && orderMetadata?.sectors, [orderMetadata?.sectors])

  useEffect(() => {
    if (sectors && !selectedSectors) {
      const allSectors = metadata.sectors
      const marketSectors = getMarketSectors(selectedSectors, allSectors)
      const market = getCurrentMarket()
      if (market && !selectedSectors.length) {
        marketSectors.push(...getMarketSectors([market], allSectors))
      }
      setSelectedSectors(marketSectors)
    }
  }, [sectors, selectedSectors])

  const handleSectorChange = (values, sectors) => {
    const updatedSectors = getMarketSectors(values, sectors)
    setSelectedSectors(updatedSectors)
    updateSetting('order-dispatch-sectors', updatedSectors)
  }

  const handlePriceTypeChange = async (priceType) => {
    setSelectedPriceType(priceType)
    updateSetting('order-dispatch-price-type', priceType)
  }

  const removeOrder = (row) => {
    queryClient.setQueriesData({ queryKey: ['getAcceptedOrdersQuery', payload] }, (cache) => {
      if (!cache) return []
      return {
        ...cache,
        rows: cache.rows.filter((order) => order.id !== row.id),
      }
    })
  }

  const updateOrder = (row) => {
    queryClient.setQueriesData({ queryKey: ['getAcceptedOrdersQuery', payload] }, (cache) => {
      if (!cache) return []
      return {
        ...cache,
        rows: cache.rows.map((order) => (order.id === row.id ? row : order)),
      }
    })
  }

  const updateOrInsertOrder = (row) => {
    queryClient.setQueriesData({ queryKey: ['getAcceptedOrdersQuery', payload] }, (cache) => {
      if (!cache) return []
      const fixedRow = { ...row, id: row.id ?? row.order_id }

      if (!cache.rows.find((order) => order.id === fixedRow.id)) {
        return { ...cache, rows: [fixedRow, ...cache.rows] }
      }
      return { ...cache, rows: cache.rows.map((order) => (order.id === fixedRow.id ? fixedRow : order)) }
    })
  }

  return (
    <AcceptedOrdersContext.Provider
      value={{
        metadata,
        orders: ordersResponse?.rows,
        ordersLoading: isLoading,
        ordersCollapsed,
        selectedSectors,
        handleSectorChange,
        setSelectedSectors,
        updateOrder,
        setOrdersCollapsed,
        removeOrder,
        updateOrInsertOrder,
        handlePriceTypeChange,
        selectedPriceType,
        refetchOrders,
        setOnlySplitCandidates: setSplitCandidatesFilter,
        onlySplitCandidates: splitCandidatesFilter,
        isManualPO: ordersResponse?.manual_po,
        isDraggingActionBlock,
        setIsDraggingActionBlock,
        isRowDragEnabled,
        setIsRowDragEnabled,
        sectors,
      }}
    >
      {children}
    </AcceptedOrdersContext.Provider>
  )
}

export const useAcceptedOrdersContext = () => {
  const context = useContext(AcceptedOrdersContext)
  if (context === undefined) {
    throw new Error('Context must be used within a Provider')
  }
  return context
}
